import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { getAllEntities } from '@adornis/baseql/operations/mongo.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission, Role } from '@adornis/buchungsheld-db/enums/roles.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { firstValueFrom, timer } from 'rxjs';
import { BHPromptAdminModify } from '../../prompts/bh-prompt-admin-modify.js';

@customElement('bh-page-admin-einrichtung-verwaltung')
export class BHPageAdminEinrichtungVerwaltung extends BuchungsheldLitElement {
  @state() private readonly institutions = new RXController(
    this,
    getAllEntities(Institution)({
      _id: 1,
      name: 1,
    }),
  );
  @state() filter = new AdornisFilter({ search: '' });

  private readonly _columns = new RXController<Array<ColumnDefinition<typeof User>>>(this, [
    {
      fieldName: 'institutions',
      selectionSet: { roles: AdornisRoleToContextsWrapper.allFields },
      renderItem: (instance: User) => {
        if (!this.institutions.value) return nothing;
        const adminRoles = instance.roles.filter(role => [Role.ADMIN, Role.SUPER_ADMIN].includes(role.name as Role));

        let institutions: Institution[] = [];
        if (adminRoles.find(role => role.name === Role.SUPER_ADMIN))
          return html`
            <x-flex wrap space="sm" horizontal>
              <tr-chip ${css({ background: this.colors.tone.success })}>
                ${this._translation.translate('management', 'global_admin')}
              </tr-chip>
            </x-flex>
          `;
        else {
          institutions = this.institutions.value.filter(i => {
            for (const role of adminRoles) {
              if (role.contexts.includes(i._id)) {
                return true;
              }
            }
            return false;
          });
        }

        return html`
          <x-flex wrap space="sm" horizontal>
            ${institutions.map(institution => html` <x-chip> ${institution.name} </x-chip> `)}
          </x-flex>
        `;
      },
    },
    {
      fieldName: 'email',
    },
    {
      columnName: '',
      customID: '_options',
      renderItem: (instance: User) => {
        return html`
          <x-flex wrap space="sm" horizontal crossaxis-center>
            <tr-button-small
              .color=${this.colors.secondary.shade(300)}
              icon="edit"
              .action=${async () => BHPromptAdminModify.edit(instance)}
            >
            </tr-button-small>
            <tr-button-small
              .color=${this.colors.secondary.shade(300)}
              icon="person"
              .action=${() =>
                XDialog.waitFor(
                  Promise.all([User.impersonate(instance._id, User._class), firstValueFrom(timer(1000))]),
                  this._translation.translate('management', 'init_imitation') as string,
                ).then(() => goTo('/admin'))}
            >
            </tr-button-small>
            <tr-button-small
              .color=${this.colors.tone.error}
              icon="delete"
              .action=${async () => {
                if (await XDialog.confirm(this._translation.translate('management', 'revoce_admin_privileges'))) {
                  const indexAdmin = instance.roles.findIndex(role => role.name === Role.ADMIN);
                  const indexGlobalAdmin = instance.roles.findIndex(role => role.name === Role.SUPER_ADMIN);
                  if (indexAdmin === -1 && indexGlobalAdmin === -1) return;
                  if (indexAdmin !== -1) instance.roles.splice(indexAdmin, 1);
                  if (indexGlobalAdmin !== -1) instance.roles.splice(indexGlobalAdmin, 1);
                  return instance.save();
                }
              }}
            >
            </tr-button-small>
          </x-flex>
        `;
      },
      columnWidth: 'max-content',
    },
  ]);

  override render() {
    return permissionGuard(Permission.VIEW_SUPER_ADMIN_SIDEBAR, () => this._content());
  }

  private _content() {
    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[{ content: 'Administrator:innen', active: true }]}
          .additionalAction=${html`
            <tr-button-small icon="add" .action=${async () => BHPromptAdminModify.add()}> </tr-button-small>
          `}
        ></bh-breadcrumb>
        <x-flex space="md">
          <tr-input
            flex
            icon="search"
            clearable
            placeholder="Durchsuchen"
            @value-picked=${(e: ValueEvent<string>) => (this.filter.search = e.detail.value)}
          ></tr-input>
          <x-data-view
            .options=${{
              class: User,
              operation: 'subscribeAdmins_prop',
              customColumns: this._columns.value,
              view: new View({ filter: this.filter, mode: 'table' }),
            } satisfies DataViewOptions<typeof User>}
          ></x-data-view>
        </x-flex>
      </x-flex>
    `;
  }
}
